import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  DailyEntryRecord,
  TRACKER_LABELS,
  TrackerId,
  TrackerSettingsRecord,
  TrackerSummaryRecord,
  WeeklyReflectionInsight,
  WeeklyReflectionRecord,
} from '../types/dailyBite';

/* ────────────────────────────────────────────────────────────────────────────
   ISO week helpers — Monday-start weeks, ISO 8601, matching what the email
   subject lines and the spec language assume ("week of April 20").
   ──────────────────────────────────────────────────────────────────────── */

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function dateKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

/**
 * ISO 8601 week number. Algorithm: pick the Thursday of the same ISO week,
 * then count weeks since the first Thursday of that ISO year.
 */
export function isoWeekIdFor(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const isoYear = d.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const weekNum = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${isoYear}-W${pad(weekNum)}`;
}

/** Monday of the ISO week containing `date`, in local time. */
export function isoWeekStart(date: Date): Date {
  const d = startOfDay(date);
  const day = d.getDay() || 7; // Sunday → 7
  d.setDate(d.getDate() - (day - 1));
  return d;
}

export function isoWeekEnd(date: Date): Date {
  const start = isoWeekStart(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return end;
}

/** "Apr 20 – 26" style label. */
export function weekRangeLabel(weekStart: string, weekEnd: string): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [, sm, sd] = weekStart.split('-').map((n, i) => (i === 0 ? n : Number(n) as any)) as [string, number, number];
  const [, em, ed] = weekEnd.split('-').map((n, i) => (i === 0 ? n : Number(n) as any)) as [string, number, number];
  const startStr = `${months[(sm as number) - 1]} ${sd}`;
  const endStr = (sm as number) === (em as number)
    ? `${ed}`
    : `${months[(em as number) - 1]} ${ed}`;
  return `${startStr} – ${endStr}`;
}

/* ────────────────────────────────────────────────────────────────────────────
   Visibility window — Sunday 18:00 local through end-of-Wednesday. After
   Wednesday we hide the tile so it doesn't get stale; the next week's
   reflection appears the following Sunday evening.
   ──────────────────────────────────────────────────────────────────────── */

export function shouldShowReflection(now: Date = new Date()): boolean {
  const day = now.getDay(); // 0 Sun … 6 Sat
  if (day === 0 && now.getHours() >= 18) return true;
  if (day >= 1 && day <= 3) return true; // Mon, Tue, Wed
  return false;
}

/**
 * The week the reflection should COVER — i.e. the most recently completed
 * Mon-Sun. On Sunday after 18:00, the current week (which ends today) is
 * "done enough" to reflect on. Mon-Wed, we look one week back.
 */
export function reflectionTargetWeekId(now: Date = new Date()): string {
  const day = now.getDay();
  const target = new Date(now);
  if (day !== 0) {
    // Mon-Sat: look at last week (the one ending the most recent Sunday).
    target.setDate(target.getDate() - day);
  }
  // Sunday: this same week.
  return isoWeekIdFor(target);
}

/* ────────────────────────────────────────────────────────────────────────────
   Doc refs
   ──────────────────────────────────────────────────────────────────────── */

function reflectionsCol(uid: string) {
  return collection(db, 'users', uid, 'weekly_reflections');
}

function reflectionRef(uid: string, weekId: string) {
  return doc(db, 'users', uid, 'weekly_reflections', weekId);
}

/* ────────────────────────────────────────────────────────────────────────────
   Per-tracker "did the member log this on this day?" predicates. Mirrors the
   completeness logic in dailyBiteService; kept in sync here because the
   reflection counts logged-vs-skipped per tracker, not the boolean
   completeness sum.
   ──────────────────────────────────────────────────────────────────────── */

function trackerLoggedOnDay(entry: DailyEntryRecord | undefined, id: TrackerId): boolean {
  if (!entry) return false;
  switch (id) {
    case 'water':
      return (entry.water?.totalMl || 0) > 0;
    case 'protein':
      return (entry.protein?.totalGrams || 0) > 0;
    case 'caffeine':
      return (entry.caffeine?.totalMg || 0) > 0;
    case 'alcohol':
      return Boolean(entry.alcohol);
    case 'nicotine':
      return Boolean(entry.nicotine);
    case 'mood':
      return Boolean(entry.mood);
    case 'gratitude':
      return Boolean(entry.gratitude && (entry.gratitude.response || '').trim().length > 0);
    case 'digestion':
      return Boolean(entry.digestion && (entry.digestion.movements || []).length > 0);
    default:
      return false;
  }
}

/* ────────────────────────────────────────────────────────────────────────────
   Lead paragraph + insights — explicitly non-judgmental copy, framed around
   what the pattern MEANS rather than scoring the week. See spec ("Phasing →
   Phase 2 → Weekly reflection") for the editorial rules.
   ──────────────────────────────────────────────────────────────────────── */

interface ReflectionMetrics {
  daysLogged: number;
  streakAtEnd: number;
  mostLoggedLabel: string | null;
  mostSkippedLabel: string | null;
  caffeineAfter3pmDays: number;
  lowMoodDays: number;
  proteinDays: number;
  highHydrationDays: number;
  gratitudeDays: number;
  dryDays: number;
  alcoholOptedIn: boolean;
}

function buildLeadParagraph(m: ReflectionMetrics): string {
  // Tier by days logged. The shape of the week matters more than perfect
  // attendance, and we want the copy to reflect that.
  if (m.daysLogged >= 7) {
    const trail = m.mostLoggedLabel
      ? ` ${m.mostLoggedLabel} showed up most consistently — the kind of quiet repetition habits are made of.`
      : '';
    return `A full week of showing up. That kind of consistency is the soil habits grow in.${trail}`;
  }
  if (m.daysLogged >= 5) {
    return `Five days this week — that's real consistency. The shape that's emerging matters more than perfect attendance.`;
  }
  if (m.daysLogged >= 3) {
    return `A few days this week. Even partial logging gives shape to a pattern; we'll see more as the weeks add up.`;
  }
  if (m.daysLogged >= 1) {
    return `Just a couple of days this week — that's still data. The days you stepped away tell us something too.`;
  }
  return `A quiet week on Daily Bite. Nothing to read into — life moves. Whenever you're ready, the next bite is here.`;
}

function buildInsights(m: ReflectionMetrics): WeeklyReflectionInsight[] {
  const out: WeeklyReflectionInsight[] = [];

  if (m.caffeineAfter3pmDays >= 3) {
    out.push({
      id: 'caffeine_after_3pm',
      text: `You had caffeine after 3pm on ${m.caffeineAfter3pmDays} of the days you logged. Worth watching against sleep quality once we layer in your wearable data.`,
    });
  }

  if (m.lowMoodDays >= 3) {
    out.push({
      id: 'low_mood_run',
      text: `Mood ran on the lower side for ${m.lowMoodDays} days. If that continues into next week, it's worth mentioning to your care team.`,
    });
  }

  if (m.proteinDays >= 4 && out.length < 2) {
    out.push({
      id: 'protein_consistent',
      text: `Protein landed in a useful range on ${m.proteinDays} days. Helpful context for the muscle and satiety conversations later on.`,
    });
  }

  if (m.highHydrationDays >= 5 && out.length < 2) {
    out.push({
      id: 'hydration_consistent',
      text: `You hit your hydration goal on ${m.highHydrationDays} days — quietly compounding work.`,
    });
  }

  if (m.gratitudeDays >= 4 && out.length < 2) {
    out.push({
      id: 'gratitude_consistent',
      text: `${m.gratitudeDays} gratitude entries this week. The research says that pattern itself moves the needle, regardless of what you wrote.`,
    });
  }

  if (m.alcoholOptedIn && m.dryDays >= 4 && out.length < 2) {
    out.push({
      id: 'dry_days_run',
      text: `${m.dryDays} dry days this week. No commentary needed — just noting the pattern.`,
    });
  }

  // Cap to 2 insights — the email and tile both look noisy beyond that.
  return out.slice(0, 2);
}

/* ────────────────────────────────────────────────────────────────────────────
   Core compute — pure-ish (uses Firestore reads) function used by both the
   client tile and the email Cloud Function. The Cloud Function re-implements
   the logic in JS but reads the same persisted result first, so there's a
   single source of truth in steady state.
   ──────────────────────────────────────────────────────────────────────── */

export interface ComputeWeeklyReflectionInput {
  uid: string;
  weekId?: string;
  /** Pre-fetched entries optional — speeds up the /today render path. */
  entries?: DailyEntryRecord[];
  settings: TrackerSettingsRecord;
  summary: TrackerSummaryRecord;
}

export async function computeAndSaveWeeklyReflection(
  input: ComputeWeeklyReflectionInput,
): Promise<WeeklyReflectionRecord> {
  const now = new Date();
  const weekId = input.weekId || reflectionTargetWeekId(now);

  // Anchor week start/end from the weekId string itself so client and server
  // always agree on the boundaries even if their local clocks drift.
  const [yearStr, weekStr] = weekId.split('-W');
  const isoYear = Number(yearStr);
  const weekNum = Number(weekStr);
  const jan4 = new Date(isoYear, 0, 4);
  const week1Monday = isoWeekStart(jan4);
  const weekStartDate = new Date(week1Monday);
  weekStartDate.setDate(week1Monday.getDate() + (weekNum - 1) * 7);
  const weekEndDate = new Date(weekStartDate);
  weekEndDate.setDate(weekStartDate.getDate() + 6);

  const weekStart = dateKey(weekStartDate);
  const weekEnd = dateKey(weekEndDate);

  // Build the list of date keys this week covers.
  const dayKeys: string[] = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(weekStartDate);
    d.setDate(weekStartDate.getDate() + i);
    dayKeys.push(dateKey(d));
  }

  let entries = input.entries;
  if (!entries) {
    const snap = await getDocs(
      query(
        collection(db, 'users', input.uid, 'daily_entries'),
        where('date', '>=', weekStart),
        where('date', '<=', weekEnd),
      ),
    );
    entries = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) } as DailyEntryRecord));
  }
  const entriesByDate = new Map<string, DailyEntryRecord>();
  for (const e of entries) entriesByDate.set(e.date, e);

  let daysLogged = 0;
  for (const k of dayKeys) {
    const e = entriesByDate.get(k);
    if (e && (e.completeness || 0) > 0) daysLogged += 1;
  }

  // Per-tracker logged counts, restricted to opted-in trackers. We also
  // separate "most logged" vs "most skipped" — the latter is the tracker
  // the member has opted in to but logged the least often this week.
  const optedInTrackers = (Object.keys(input.settings.optedIn) as TrackerId[]).filter(
    (id) => input.settings.optedIn[id],
  );
  let mostLoggedTracker: TrackerId | null = null;
  let mostLoggedCount = -1;
  let mostSkippedTracker: TrackerId | null = null;
  let mostSkippedCount = 8; // any tracker has ≤ 7 logged days
  for (const id of optedInTrackers) {
    let count = 0;
    for (const k of dayKeys) {
      if (trackerLoggedOnDay(entriesByDate.get(k), id)) count += 1;
    }
    if (count > mostLoggedCount) {
      mostLoggedCount = count;
      mostLoggedTracker = id;
    }
    if (count < mostSkippedCount) {
      mostSkippedCount = count;
      mostSkippedTracker = id;
    }
  }
  // If the most-logged and most-skipped collapse onto the same tracker (e.g.
  // a brand-new account with 0 days), don't surface either.
  if (mostLoggedTracker === mostSkippedTracker) {
    mostLoggedTracker = null;
    mostSkippedTracker = null;
  }

  // Insight signals — only computed for opted-in trackers.
  let caffeineAfter3pmDays = 0;
  let lowMoodDays = 0;
  let proteinDays = 0;
  let highHydrationDays = 0;
  let gratitudeDays = 0;
  let dryDays = 0;
  for (const k of dayKeys) {
    const e = entriesByDate.get(k);
    if (!e) continue;
    if (input.settings.optedIn.caffeine && e.caffeine?.lastAt) {
      const d = (e.caffeine.lastAt as Timestamp).toDate();
      if (d.getHours() >= 15) caffeineAfter3pmDays += 1;
    }
    if (input.settings.optedIn.mood && e.mood && (e.mood.valence || 0) <= 2) {
      lowMoodDays += 1;
    }
    if (input.settings.optedIn.protein && (e.protein?.totalGrams || 0) >= 100) {
      proteinDays += 1;
    }
    if (input.settings.optedIn.water && (e.water?.totalMl || 0) >= 2000) {
      highHydrationDays += 1;
    }
    if (
      input.settings.optedIn.gratitude &&
      e.gratitude &&
      (e.gratitude.response || '').trim().length > 0
    ) {
      gratitudeDays += 1;
    }
    if (input.settings.optedIn.alcohol && (!e.alcohol || (e.alcohol.units || 0) === 0)) {
      dryDays += 1;
    }
  }

  const metrics: ReflectionMetrics = {
    daysLogged,
    streakAtEnd: input.summary.currentStreak || 0,
    mostLoggedLabel: mostLoggedTracker ? TRACKER_LABELS[mostLoggedTracker] : null,
    mostSkippedLabel: mostSkippedTracker ? TRACKER_LABELS[mostSkippedTracker] : null,
    caffeineAfter3pmDays,
    lowMoodDays,
    proteinDays,
    highHydrationDays,
    gratitudeDays,
    dryDays,
    alcoholOptedIn: input.settings.optedIn.alcohol,
  };

  const leadParagraph = buildLeadParagraph(metrics);
  const insights = buildInsights(metrics);

  const ref = reflectionRef(input.uid, weekId);
  const existing = await getDoc(ref);
  const existingData = existing.exists() ? (existing.data() as Partial<WeeklyReflectionRecord>) : null;

  // Preserve dismissedAt + emailedAt if a prior version exists so that
  // re-computation (e.g. mid-week after another save) doesn't un-dismiss the
  // tile or trigger a duplicate email.
  const next: WeeklyReflectionRecord = {
    weekId,
    weekStart,
    weekEnd,
    daysLogged,
    streakAtEnd: metrics.streakAtEnd,
    mostLoggedTracker,
    mostSkippedTracker,
    leadParagraph,
    insights,
    generatedAt: existingData?.generatedAt ?? null,
    dismissedAt: existingData?.dismissedAt ?? null,
    emailedAt: existingData?.emailedAt ?? null,
  };

  await setDoc(
    ref,
    {
      ...next,
      generatedAt: existingData?.generatedAt ? existingData.generatedAt : serverTimestamp(),
    },
    { merge: true },
  );

  return next;
}

/* ────────────────────────────────────────────────────────────────────────────
   Read / dismiss
   ──────────────────────────────────────────────────────────────────────── */

export async function getWeeklyReflection(
  uid: string,
  weekId: string,
): Promise<WeeklyReflectionRecord | null> {
  const snap = await getDoc(reflectionRef(uid, weekId));
  if (!snap.exists()) return null;
  const data = snap.data() as Partial<WeeklyReflectionRecord>;
  return {
    weekId: data.weekId || weekId,
    weekStart: data.weekStart || '',
    weekEnd: data.weekEnd || '',
    daysLogged: data.daysLogged || 0,
    streakAtEnd: data.streakAtEnd || 0,
    mostLoggedTracker: data.mostLoggedTracker ?? null,
    mostSkippedTracker: data.mostSkippedTracker ?? null,
    leadParagraph: data.leadParagraph || '',
    insights: data.insights || [],
    generatedAt: data.generatedAt ?? null,
    dismissedAt: data.dismissedAt ?? null,
    emailedAt: data.emailedAt ?? null,
  };
}

export async function dismissWeeklyReflection(uid: string, weekId: string): Promise<void> {
  await updateDoc(reflectionRef(uid, weekId), {
    dismissedAt: serverTimestamp(),
  });
}

export async function listRecentWeeklyReflections(
  uid: string,
  max: number = 8,
): Promise<WeeklyReflectionRecord[]> {
  const snap = await getDocs(
    query(reflectionsCol(uid), orderBy('weekStart', 'desc'), limit(max)),
  );
  return snap.docs.map((d) => {
    const data = d.data() as Partial<WeeklyReflectionRecord>;
    return {
      weekId: data.weekId || d.id,
      weekStart: data.weekStart || '',
      weekEnd: data.weekEnd || '',
      daysLogged: data.daysLogged || 0,
      streakAtEnd: data.streakAtEnd || 0,
      mostLoggedTracker: data.mostLoggedTracker ?? null,
      mostSkippedTracker: data.mostSkippedTracker ?? null,
      leadParagraph: data.leadParagraph || '',
      insights: data.insights || [],
      generatedAt: data.generatedAt ?? null,
      dismissedAt: data.dismissedAt ?? null,
      emailedAt: data.emailedAt ?? null,
    };
  });
}
